import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";
import { getZoneColor } from "../../utils/zoneUtils";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
} from "recharts";
import {
  FiUsers,
  FiHeart,
  FiAlertCircle,
  FiCheckCircle,
  FiBell,
  FiBarChart2,
  FiChevronRight,
  FiHome,
  FiChevronUp,
  FiChevronDown,
  FiActivity,
  FiClock,
  FiTrendingUp,
  FiCpu,
  FiZap,
  FiGlobe,
  FiSliders,
  FiFilter,
  FiCheck,
} from "react-icons/fi";

// ─── Types ──────────────────────────────────────────────────────────────────

interface CategoryStat {
  catId: number;
  catName: string;
  avgScore: number;
  logCount: number;
  zone: string;
}

interface TimelinePoint {
  date: string;
  score: number;
  logCount: number;
}

interface RecentLog {
  id: number;
  date: string;
  time: string;
  calculatedEmotion: string;
  calculatedZone: string;
  category?: { name: string };
}

// Phase 1 — core data (loads first, fast)
interface ChildCore {
  id: number;
  name: string;
  grade: string;
  school: string;
  ensightlyScore: number | null;
  zone: string;
  totalLogs: number;
  lastLogged: string;
  needsAttention: boolean;
}

// Phase 2 — enrichment data (loads in background)
interface ChildEnrichment {
  categories: CategoryStat[];
  timeline: TimelinePoint[];
  recentLogs: RecentLog[];
}

type Child = ChildCore & Partial<ChildEnrichment>;

type Period = "month" | "year" | "all";

const PERIOD_API: Record<Period, string> = {
  month: "current-month",
  year:  "year",
  all:   "all",
};

// Zone colours: green=#10b981, blue=#3b82f6, yellow=#f59e0b, brown=#8b4513, red=#ef4444
// Child line colours are deliberately chosen to NOT clash with any zone colour:
const CHILD_COLORS = ["#8b5cf6", "#f97316", "#ec4899", "#14b8a6", "#6366f1"];

// ─── Helpers ────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string, timeStr?: string): string {
  const dt = new Date(`${dateStr}T${timeStr || "00:00:00"}`);
  const diffMs = Date.now() - dt.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function scoreToPercent(score: number | null): number {
  if (!score) return 0;
  return Math.round((score / 7) * 100);
}

const ZoneBadge: React.FC<{ zone: string }> = ({ zone }) => {
  const { color } = getZoneColor(zone);
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: `${color}22`, color }}
    >
      {zone || "No Zone"}
    </span>
  );
};

const CategoryBar: React.FC<{ cat: CategoryStat }> = ({ cat }) => {
  const { color } = getZoneColor(cat.zone);
  const pct = scoreToPercent(cat.avgScore);
  return (
    <div className="flex items-center gap-3 mb-1.5">
      <span className="text-xs text-slate-500 w-20 truncate flex-shrink-0">{cat.catName}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-1.5">
        <div
          className="h-1.5 rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span className="text-xs font-semibold text-slate-600 w-8 text-right">{pct}%</span>
    </div>
  );
};

// Skeleton shimmer block
const Shimmer: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`bg-slate-200 rounded animate-pulse ${className}`} />
);

// ── AI Insight generator (rule-based templates, zone + trend + score) ────────
function generateInsight(child: Child, trend: "up" | "down" | "stable"): {
  headline: string;
  body: string;
  action: string;
  tone: "positive" | "neutral" | "caution" | "alert";
} {
  const firstName = child.name.split(" ")[0];
  const zone = (child.zone || "").toLowerCase();
  const score = child.ensightlyScore ?? 0;
  const pct = Math.round((score / 7) * 100);

  if (zone.includes("green")) {
    const headlines = ["Thriving ✨", "Top wellbeing 💫", "Doing really well 🌟"];
    return {
      headline: headlines[Math.floor(pct / 34) % headlines.length],
      body: trend === "up"
        ? `${firstName} is in the Green Zone and improving. Their emotional wellbeing score (${pct}%) is one of their strongest. Keep the positive momentum going!`
        : trend === "down"
        ? `${firstName} is still in the Green Zone (${pct}%) but showing a slight dip. This is normal — just stay connected and check in regularly.`
        : `${firstName} is consistently thriving in the Green Zone at ${pct}%. No concerns — they are emotionally balanced and engaged.`,
      action: "Keep encouraging open conversations about their day.",
      tone: "positive",
    };
  }

  if (zone.includes("blue")) {
    return {
      headline: trend === "up" ? "On the rise 📈" : trend === "down" ? "Slight dip 📉" : "Stable 🗡️",
      body: trend === "up"
        ? `${firstName}'s score has been climbing (${pct}%). They're in the Blue Zone — just one step from their best. Consistent support is working.`
        : trend === "down"
        ? `${firstName} is in the Blue Zone (${pct}%) but trending down slightly. Monitor over the next few weeks to see if it continues.`
        : `${firstName} is holding steady in the Blue Zone at ${pct}%. This is a healthy, calm state — no immediate concerns.`,
      action: "Celebrate their consistency. Ask about what they enjoy at school.",
      tone: "positive",
    };
  }

  if (zone.includes("yellow")) {
    return {
      headline: trend === "up" ? "Recovering 🏋" : trend === "down" ? "Needs attention ⚠️" : "Mixed signals 🟡",
      body: trend === "up"
        ? `${firstName} is recovering well from the Yellow Zone (${pct}%). Their score is improving — they may have worked through a difficult patch.`
        : trend === "down"
        ? `${firstName}'s score has been declining and is now in the Yellow Zone (${pct}%). This signals emotional stress that is building up. Check in with them this week.`
        : `${firstName} is in the Yellow Zone (${pct}%). They seem emotionally unsettled — not critical, but worth paying attention to.`,
      action: "Have a calm, non-judgmental conversation. Ask how school and friendships are going.",
      tone: "caution",
    };
  }

  if (zone.includes("brown")) {
    return {
      headline: trend === "up" ? "Improving 📈" : trend === "down" ? "Declining 😟" : "Struggling 😔",
      body: trend === "down"
        ? `${firstName}'s wellbeing score is declining and has dropped into the Brown Zone (${pct}%). This is a sign of sustained emotional burden — please reach out to them soon.`
        : trend === "up"
        ? `${firstName} is in the Brown Zone (${pct}%) but recovering. They may be working through something — continued support is important right now.`
        : `${firstName} is in the Brown Zone (${pct}%). They are experiencing a difficult emotional period. A gentle check-in could make a big difference.`,
      action: "Reach out today. Create space for them to share without pressure.",
      tone: "alert",
    };
  }

  if (zone.includes("red")) {
    return {
      headline: trend === "up" ? "Early recovery ❤️" : "Needs support now 🙏",
      body: trend === "up"
        ? `${firstName} is in the Red Zone (${pct}%) but their score is beginning to improve. Early intervention seems to be helping — keep the support going.`
        : `${firstName}'s wellbeing score is in the Red Zone (${pct}%). This level of emotional distress needs immediate attention. Please make time to connect with them today.`,
      action: "Prioritise connecting with them today. Consider speaking to their school counsellor.",
      tone: "alert",
    };
  }

  if (zone.includes("dark")) {
    return {
      headline: "Urgent attention needed 🚨",
      body: `${firstName} is in the Dark Red Zone (${pct}%). This is a serious signal of deep emotional distress. Please connect with them immediately and consider professional support.`,
      action: "Contact school counsellor or a mental health professional as soon as possible.",
      tone: "alert",
    };
  }

  return {
    headline: "No data yet 📋",
    body: `${firstName} hasn't logged enough mood data yet. Encourage them to check in through the Sereneminds app a few times a week.`,
    action: "Remind them to log their mood today.",
    tone: "neutral",
  };
}

// Backend DB thresholds on 0-100 scale (joyfulness 1-7 → score = (joy/7)*100):
//  ≥ 88  → Green Zone   (joy ≥ 6.16)
//  ≥ 75  → Blue Zone    (joy ≥ 5.25)
//  ≥ 63  → Yellow Zone  (joy ≥ 4.41)
//  ≥ 38  → Brown Zone   (joy ≥ 2.66)
//  ≥ 25  → Red Zone     (joy ≥ 1.75)
//  < 25  → Dark Red     (joy < 1.75)
//
// Joyfulness boundaries (1-7 scale used in chart domain):
const JOY_ZONES = [
  { name: "Green Zone", color: "#10b981", min: 6.16 },
  { name: "Blue Zone",  color: "#3b82f6", min: 5.25 },
  { name: "Yellow Zone",color: "#f59e0b", min: 4.41 },
  { name: "Brown Zone", color: "#8b4513", min: 2.66 },
  { name: "Red Zone",   color: "#ef4444", min: 1.75 },
  { name: "Dark Red",   color: "#dc2626", min: 0    },
] as const;

function scoreToZoneLabel(score: number): { name: string; color: string } {
  for (const z of JOY_ZONES) {
    if (score >= z.min) return { name: z.name, color: z.color };
  }
  return { name: "Dark Red", color: "#dc2626" };
}

const ZoneYTick = ({ x, y, payload }: any) => {
  const { name, color } = scoreToZoneLabel(payload.value);
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={4} textAnchor="end" fill={color} fontSize={10} fontWeight={600}>
        {name}
      </text>
    </g>
  );
};

const BENCH_GRADE_OPTIONS = [
  "All Grades", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5",
  "Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12"
];
const BENCH_BOARD_OPTIONS = ["All Boards", "CBSE", "ICSE", "State Board", "IB", "Cambridge"];
const BENCH_COUNTRY_OPTIONS = ["All Countries", "India"];
const BENCH_STATE_OPTIONS = [
  "All States", "Tamil Nadu", "Maharashtra", "Karnataka", "Delhi",
  "Kerala", "Telangana", "Andhra Pradesh", "Gujarat"
];
const BENCH_AREA_OPTIONS = ["All Areas", "Urban", "Semi-Urban", "Rural"];

const ChartFilterDropdown: React.FC<{
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}> = ({ label, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="flex flex-col gap-1 text-xs" ref={ref}>
      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
        {label}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center justify-between gap-1.5 min-w-[115px] w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 hover:bg-white hover:border-[#1ecab8]/50 transition-all shadow-xs"
        >
          <span className="truncate max-w-[90px]">{value}</span>
          <FiChevronDown
            size={12}
            className={`text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && (
          <div className="absolute top-[calc(100%+4px)] left-0 min-w-full bg-white border border-slate-100 shadow-xl rounded-xl z-50 p-1 max-h-48 overflow-y-auto">
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                className={`w-full text-left px-2.5 py-1.5 rounded-md text-xs transition-colors ${
                  value === opt
                    ? "bg-[#1ecab8]/10 text-[#1ecab8] font-bold"
                    : "text-slate-700 hover:bg-slate-50 font-medium"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TrendTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 shadow-xl text-xs space-y-1.5 min-w-[175px]">
      <div className="font-bold text-slate-800 border-b border-slate-100 pb-1 flex items-center justify-between">
        <span>{label}</span>
        <span className="text-[10px] text-slate-400 font-normal">Monthly Avg</span>
      </div>
      <div className="space-y-1.5 pt-0.5">
        {payload.map((p: any) => {
          const val = p.value;
          const zone = val != null ? scoreToZoneLabel(val) : null;
          const isEnsightly = p.dataKey === "ensightlyAvg" || p.name === "Ensightly Average";
          const color = isEnsightly ? "#0f172a" : p.stroke || p.color;
          return (
            <div key={p.dataKey || p.name} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <span
                  className={`w-2.5 h-2.5 rounded-full shrink-0 ${isEnsightly ? "border border-slate-400" : ""}`}
                  style={{ background: color }}
                />
                <span className="text-slate-700 font-semibold">{p.name}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-black text-slate-800">
                  {typeof val === "number" ? val.toFixed(1) : val}/7
                </span>
                {zone && (
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded-md"
                    style={{ color: zone.color, background: `${zone.color}18` }}
                  >
                    {zone.name}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const ParentDashboard: React.FC = () => {
  const { user } = useAuth();
  // Parent model has 'name' (not firstName/lastName)
  const parentName = (user?.name || user?.firstName || "Parent").split(" ")[0];

  const [children, setChildren] = useState<Child[]>([]);
  const [coreLoading, setCoreLoading] = useState(true);   // Phase 1 spinner
  const [enrichLoading, setEnrichLoading] = useState(false); // Phase 2 quiet
  const [period, setPeriod] = useState<Period>("all");
  const [selectedChildIds, setSelectedChildIds] = useState<Set<number>>(new Set());
  const [aiInsightChildId, setAiInsightChildId] = useState<number | null>(null);
  const enrichRef = useRef<AbortController | null>(null);

  // Ensightly Average Benchmark state
  const [showEnsightlyAvg, setShowEnsightlyAvg] = useState(false);
  const [benchCountry, setBenchCountry] = useState("All Countries");
  const [benchState, setBenchState] = useState("All States");
  const [benchArea, setBenchArea] = useState("All Areas");
  const [benchBoard, setBenchBoard] = useState("All Boards");
  const [benchGrade, setBenchGrade] = useState("All Grades");

  const [ensightlyTimeline, setEnsightlyTimeline] = useState<TimelinePoint[]>([]);
  const [benchLoading, setBenchLoading] = useState(false);

  const apiPeriod = PERIOD_API[period];

  // ── PHASE 1: Students + consolidated score only (fast) ──────────────────────
  const fetchCore = useCallback(async () => {
    if (!user?.email) return;
    setCoreLoading(true);
    try {
      // Fetch students via authenticated JWT endpoint (server-side email matching)
      const childrenToken = localStorage.getItem("token");
      let students: any[] = [];
      try {
        const childrenRes = await axios.get(`${API_BASE_URL}/parent-auth/children`, {
          headers: childrenToken ? { Authorization: `Bearer ${childrenToken}` } : {},
        });
        students = childrenRes.data?.data || [];
      } catch (err) {
        console.warn("[ParentDashboard] API /children failed, using fallback data.");
      }

      // Filter out any leftover mock data and limit to 3 students for testing
      students = students.filter(s => !s.firstName?.toLowerCase().includes('mock') && !s.lastName?.toLowerCase().includes('external'));
      
      // Limit to 3 students for testing
      students = students.slice(0, 3);
      
      console.log("[ParentDashboard] children fetched:", students.length, students);

      // Fetch consolidated scores for all children in parallel (1 call each)
      const coreData: ChildCore[] = await Promise.all(
        students.map(async (s: any) => {
          let consolidated: any = null;
          let lastLogged = "Never";
          try {
            const r = await axios.get(
              `${API_BASE_URL}/student-mood-logs/consolidated/${s.id}`,
              { params: { period: apiPeriod } }
            );
            consolidated = r.data;
          } catch {}

          // Fetch just the most recent log for "last logged" text
          try {
            const r = await axios.get(`${API_BASE_URL}/student-mood-logs`, {
              params: { studentId: s.id, status: true, limit: 1 },
            });
            const logs = r.data || [];
            if (logs.length > 0) lastLogged = timeAgo(logs[0].date, logs[0].time);
          } catch {}

          const zone = consolidated?.zone || "No Zone";
          const zoneCheck = zone.toLowerCase();
          const needsAttention =
            zoneCheck.includes("yellow") || zoneCheck.includes("red") ||
            zoneCheck.includes("brown") || zoneCheck.includes("orange");

          return {
            id: s.id,
            name: `${s.firstName} ${s.lastName}`,
            grade: s.className || "N/A",
            school: s.school?.name || "N/A",
            ensightlyScore: consolidated?.avgScore ?? null,
            zone,
            totalLogs: consolidated?.logCount ?? 0,
            lastLogged,
            needsAttention,
          };
        })
      );

      setChildren(coreData);
    } catch (err) {
      console.error("Error fetching core data:", err);
    } finally {
      setCoreLoading(false);
    }
  }, [user, apiPeriod]);

  // ── PHASE 2: Enrichment (wellbeing, timeline, recent logs) — runs quietly ──
  const fetchEnrichment = useCallback(async (coreChildren: ChildCore[]) => {
    if (coreChildren.length === 0) return;

    // Cancel any previous enrichment fetch
    enrichRef.current?.abort();
    enrichRef.current = new AbortController();
    const signal = enrichRef.current.signal;

    setEnrichLoading(true);
    try {
      await Promise.all(
        coreChildren.map(async (child) => {
          try {
            const [wellbeingRes, timelineRes, logsRes] = await Promise.allSettled([
              axios.get(`${API_BASE_URL}/student-mood-logs/wellbeing/${child.id}`, {
                params: { period: apiPeriod },
                signal,
              }),
              axios.get(`${API_BASE_URL}/student-mood-logs/ensightly/${child.id}`, {
                params: { period: apiPeriod },
                signal,
              }),
              axios.get(`${API_BASE_URL}/student-mood-logs`, {
                params: { studentId: child.id, status: true, limit: 5 },
                signal,
              }),
            ]);

            if (signal.aborted) return;

            const categories: CategoryStat[] =
              wellbeingRes.status === "fulfilled"
                ? (wellbeingRes.value.data.data || []).filter((c: CategoryStat) => c.avgScore > 0)
                : [];
            const timeline: TimelinePoint[] =
              timelineRes.status === "fulfilled"
                ? timelineRes.value.data.timeline || []
                : [];
            const recentLogs: RecentLog[] =
              logsRes.status === "fulfilled"
                ? (logsRes.value.data || []).slice(0, 5)
                : [];

            // Merge enrichment into existing child entry
            setChildren((prev) =>
              prev.map((c) =>
                c.id === child.id ? { ...c, categories, timeline, recentLogs } : c
              )
            );
          } catch (e: any) {
            if (e.name !== "CanceledError" && e.name !== "AbortError") {
              console.warn(`Enrichment failed for child ${child.id}`, e);
            }
          }
        })
      );
    } finally {
      setEnrichLoading(false);
    }
  }, [apiPeriod]);

  // Fetch Ensightly Benchmark Timeline when enabled or filters change
  const fetchBenchmarkTimeline = useCallback(async () => {
    if (!showEnsightlyAvg) return;
    setBenchLoading(true);
    try {
      const params: any = { period: apiPeriod };
      if (benchCountry !== "All Countries") params.country = benchCountry;
      if (benchState   !== "All States")    params.state   = benchState;
      if (benchArea    !== "All Areas")     params.area    = benchArea;
      if (benchBoard   !== "All Boards")    params.board   = benchBoard;
      if (benchGrade   !== "All Grades")    params.grade   = benchGrade;

      const res = await axios.get(`${API_BASE_URL}/student-mood-logs/benchmark-timeline`, { params });
      setEnsightlyTimeline(res.data?.timeline || []);
    } catch (err) {
      console.warn("[ParentDashboard] Failed to fetch benchmark timeline", err);
      setEnsightlyTimeline([]);
    } finally {
      setBenchLoading(false);
    }
  }, [showEnsightlyAvg, apiPeriod, benchCountry, benchState, benchArea, benchBoard, benchGrade]);

  useEffect(() => {
    fetchBenchmarkTimeline();
  }, [fetchBenchmarkTimeline]);

  // Run Phase 1 on mount + period change
  useEffect(() => {
    fetchCore();
  }, [fetchCore]);

  // Run Phase 2 after Phase 1 completes
  useEffect(() => {
    if (!coreLoading && children.length > 0) {
      fetchEnrichment(children as ChildCore[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coreLoading]);

  // Default: pre-select the first child once core data loads
  useEffect(() => {
    if (!coreLoading && children.length > 0 && selectedChildIds.size === 0) {
      setSelectedChildIds(new Set([children[0].id]));
      setAiInsightChildId(children[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coreLoading, children]);

  // Keep AI insight child in sync with chart child selection
  useEffect(() => {
    const firstSelected = Array.from(selectedChildIds)[0];
    if (firstSelected != null) setAiInsightChildId(firstSelected);
  }, [selectedChildIds]);

  // ── Derived stats ────────────────────────────────────────────────────────
  const totalLogs = children.reduce((s, c) => s + c.totalLogs, 0);
  const activeChildren = children.filter((c) => c.totalLogs > 0).length;
  const attentionCount = children.filter((c) => c.needsAttention).length;

  const allRecentActivity = children
    .flatMap((c) =>
      (c.recentLogs || []).map((l) => ({
        childName: c.name,
        childIdx: children.indexOf(c),
        ...l,
      }))
    )
    .sort((a, b) => {
      const da = new Date(`${a.date}T${a.time || "00:00:00"}`).getTime();
      const db = new Date(`${b.date}T${b.time || "00:00:00"}`).getTime();
      return db - da;
    })
    .slice(0, 8);

  const alerts: { msg: string; type: "warning" | "info" }[] = [];
  children.forEach((c) => {
    if (c.needsAttention)
      alerts.push({ msg: `${c.name.split(" ")[0]} is in the ${c.zone} — check in with them.`, type: "warning" });
    if (c.totalLogs === 0)
      alerts.push({ msg: `${c.name.split(" ")[0]} hasn't logged any moods this period.`, type: "info" });
  });

  // Only children the parent has toggled ON in the chart
  const visibleChildren = children.filter((c) => selectedChildIds.has(c.id));

  // Multi-child toggle: allows selecting 1, 2 or all children
  const toggleChild = (id: number) => {
    setSelectedChildIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size > 1 || showEnsightlyAvg) {
          next.delete(id);
        }
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAllChildren = () => {
    setSelectedChildIds(new Set(children.map((c) => c.id)));
  };

  // ── Monthly aggregated chart data for multi-child + Ensightly benchmark (memoised) ──
  const monthlyData = useMemo(() => {
    const monthKeysSet = new Set<string>();

    visibleChildren.forEach((child) => {
      (child.timeline || []).forEach((pt) => {
        monthKeysSet.add(pt.date.slice(0, 7)); // "YYYY-MM"
      });
    });

    if (showEnsightlyAvg && ensightlyTimeline.length > 0) {
      ensightlyTimeline.forEach((pt) => {
        monthKeysSet.add(pt.date.slice(0, 7));
      });
    }

    if (monthKeysSet.size === 0) return [];

    const sortedMonths = Array.from(monthKeysSet).sort();

    return sortedMonths.map((mKey) => {
      const d = new Date(mKey + "-01");
      const label =
        d.toLocaleDateString("en-US", { month: "short" }) +
        " '" +
        String(d.getFullYear()).slice(2);

      const row: Record<string, any> = { month: mKey, label };

      // Each visible child's average score for this month
      visibleChildren.forEach((child) => {
        const pts = (child.timeline || []).filter(
          (pt) => pt.date.slice(0, 7) === mKey
        );
        if (pts.length > 0) {
          const sum = pts.reduce((acc, p) => acc + p.score, 0);
          row[`child_${child.id}`] = parseFloat((sum / pts.length).toFixed(3));
        }
      });

      // Ensightly benchmark average score for this month
      if (showEnsightlyAvg) {
        const benchPts = ensightlyTimeline.filter(
          (pt) => pt.date.slice(0, 7) === mKey
        );
        if (benchPts.length > 0) {
          const sum = benchPts.reduce((acc, p) => acc + p.score, 0);
          row["ensightlyAvg"] = parseFloat((sum / benchPts.length).toFixed(3));
        }
      }

      return row;
    });
  }, [visibleChildren, showEnsightlyAvg, ensightlyTimeline]);

  const activeBenchmarkFilters = useMemo(() => {
    const list: string[] = [];
    if (benchCountry !== "All Countries") list.push(`Country: ${benchCountry}`);
    if (benchState !== "All States") list.push(`State: ${benchState}`);
    if (benchArea !== "All Areas") list.push(`Area: ${benchArea}`);
    if (benchBoard !== "All Boards") list.push(`Board: ${benchBoard}`);
    if (benchGrade !== "All Grades") list.push(`Grade: ${benchGrade}`);
    return list;
  }, [benchCountry, benchState, benchArea, benchBoard, benchGrade]);

  // Single-child summary stats (used when exactly 1 child is selected and Ensightly benchmark is off)
  const singleChild = visibleChildren.length === 1 ? visibleChildren[0] : null;

  const singleChildMonthly = useMemo(() => {
    if (!singleChild?.timeline?.length) return [];
    const buckets: Record<string, { sum: number; count: number }> = {};
    singleChild.timeline.forEach((pt) => {
      const key = pt.date.slice(0, 7);
      if (!buckets[key]) buckets[key] = { sum: 0, count: 0 };
      buckets[key].sum   += pt.score;
      buckets[key].count += 1;
    });
    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, { sum, count }]) => {
        const avg = sum / count;
        const d   = new Date(key + "-01");
        const label = d.toLocaleDateString("en-US", { month: "short" }) + " '" + String(d.getFullYear()).slice(2);
        return { month: key, label, score: parseFloat(avg.toFixed(3)) };
      });
  }, [singleChild]);

  const bestMonth   = useMemo(() => singleChildMonthly.length ? singleChildMonthly.reduce((a, b) => a.score >= b.score ? a : b) : null, [singleChildMonthly]);
  const worstMonth  = useMemo(() => singleChildMonthly.length ? singleChildMonthly.reduce((a, b) => a.score <= b.score ? a : b) : null, [singleChildMonthly]);
  const latestMonth = singleChildMonthly.length ? singleChildMonthly[singleChildMonthly.length - 1] : null;

  const trendDir: "up" | "down" | "stable" = useMemo(() => {
    if (singleChildMonthly.length < 2) return "stable";
    const diff = singleChildMonthly[singleChildMonthly.length - 1].score - singleChildMonthly[0].score;
    if (diff > 0.15) return "up";
    if (diff < -0.15) return "down";
    return "stable";
  }, [singleChildMonthly]);

  // ── Phase 1 full-page loading ────────────────────────────────────────────
  if (coreLoading) {
    return (
      <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
        {/* Breadcrumbs skeleton */}
        <Shimmer className="h-4 w-48 mb-6" />
        {/* Greeting skeleton */}
        <div className="bg-white rounded-2xl p-8 mb-6 shadow-sm border border-slate-200/80">
          <Shimmer className="h-8 w-64 mb-3" />
          <Shimmer className="h-4 w-80 mb-6" />
          <div className="flex gap-8">
            <Shimmer className="h-8 w-12" />
            <Shimmer className="h-8 w-16" />
            <Shimmer className="h-8 w-12" />
          </div>
        </div>
        {/* Stats skeleton */}
        <div className="grid grid-cols-4 gap-5 mb-8">
          {[0,1,2,3].map(i => (
            <div key={i} className="bg-white rounded-xl p-6 border border-slate-200/80">
              <Shimmer className="h-4 w-24 mb-3" />
              <Shimmer className="h-8 w-12 mb-1" />
              <Shimmer className="h-3 w-20" />
            </div>
          ))}
        </div>
        {/* Cards skeleton */}
        <div className="grid grid-cols-[2fr_1fr] gap-6">
          <div className="space-y-4">
            {[0,1].map(i => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200/80">
                <div className="flex items-start gap-4">
                  <Shimmer className="w-12 h-12 rounded-full" />
                  <div className="flex-1">
                    <Shimmer className="h-5 w-40 mb-2" />
                    <Shimmer className="h-4 w-28 mb-3" />
                    <Shimmer className="h-3 w-full mb-1" />
                    <Shimmer className="h-3 w-full mb-1" />
                    <Shimmer className="h-3 w-3/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6 border border-slate-200/80">
              <Shimmer className="h-5 w-32 mb-4" />
              {[0,1,2,3].map(i => (
                <div key={i} className="flex gap-3 mb-4">
                  <Shimmer className="w-9 h-9 rounded-lg flex-shrink-0" />
                  <div className="flex-1">
                    <Shimmer className="h-4 w-24 mb-1" />
                    <Shimmer className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Full render ──────────────────────────────────────────────────────────
  return (
    <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">

      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm text-slate-500 mb-6 font-medium">
        <FiHome className="text-[#1ecab8]" size={16} />
        <span className="text-[#1ecab8]">Parent</span>
        <FiChevronRight size={14} className="text-slate-400" />
        <span className="text-[#1ecab8]">Dashboard</span>
      </div>

      {/* Greeting */}
      <div className="bg-white rounded-2xl p-8 mb-6 shadow-sm border border-slate-200/80">
        <div className="flex flex-col lg:flex-row justify-between items-start gap-6">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Welcome back, {parentName}! 👋
            </h1>
            <p className="text-slate-500 mb-5">
              Monitor your children's wellbeing and track their emotional progress
            </p>
            <div className="flex gap-8">
              <div>
                <span className="text-xs text-slate-400 uppercase tracking-wide block mb-1">Children</span>
                <span className="text-2xl font-bold text-slate-900">{children.length}</span>
              </div>
              <div>
                <span className="text-xs text-slate-400 uppercase tracking-wide block mb-1">Active Tracking</span>
                <span className="text-2xl font-bold text-slate-900">{activeChildren}/{children.length}</span>
              </div>
              <div>
                <span className="text-xs text-slate-400 uppercase tracking-wide block mb-1">Total Logs</span>
                <span className="text-2xl font-bold text-slate-900">{totalLogs}</span>
              </div>
            </div>
          </div>
          <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-6 flex items-center gap-4 flex-shrink-0">
            <div className="w-14 h-14 rounded-xl bg-teal-100 flex items-center justify-center">
              <FiUsers size={26} className="text-teal-600" />
            </div>
            <div>
              <div className="text-xs text-teal-600 font-semibold mb-1">Family Overview</div>
              <div className="text-lg font-bold text-teal-700">
                {attentionCount === 0
                  ? "All Children Active"
                  : `${attentionCount} Need${attentionCount > 1 ? "" : "s"} Attention`}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center mb-5">
        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">SHOWING DATA FOR</div>
        <div className="ml-auto flex items-center gap-3">
          <div className="flex border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm">
            <button className="px-2 py-1.5 hover:bg-slate-50 border-r border-slate-200 text-slate-500">
              <FiChevronUp size={15} />
            </button>
            <button className="px-2 py-1.5 hover:bg-slate-50 text-slate-500">
              <FiChevronDown size={15} />
            </button>
          </div>
          <div className="flex bg-white p-1 rounded-lg border border-slate-200 shadow-sm gap-1">
            {(["month", "year", "all"] as Period[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  period === p ? "bg-[#1ecab8] text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {p === "all" ? "All Time" : p === "month" ? "Month" : "Year"}
              </button>
            ))}
          </div>
          {enrichLoading && (
            <div className="w-4 h-4 border-2 border-[#1ecab8] border-t-transparent rounded-full animate-spin" />
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {[
          { title: "Total Children", value: children.length, subtitle: "registered", icon: <FiUsers size={22} />, color: "#1ecab8" },
          { title: "Active Tracking", value: activeChildren, subtitle: "logging this period", icon: <FiCheckCircle size={22} />, color: "#10b981" },
          { title: "Needs Attention", value: attentionCount, subtitle: "in distress zone", icon: <FiAlertCircle size={22} />, color: attentionCount > 0 ? "#f59e0b" : "#10b981" },
          { title: "Total Mood Logs", value: totalLogs, subtitle: "entries logged", icon: <FiHeart size={22} />, color: "#ee5a6f" },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-3">
              <span className="text-sm font-semibold text-slate-500">{stat.title}</span>
              <span style={{ color: stat.color }}>{stat.icon}</span>
            </div>
            <div className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
            <div className="text-xs text-slate-400">{stat.subtitle}</div>
          </div>
        ))}
      </div>

      {/* Smart Alerts */}
      {alerts.length > 0 && (
        <div className="mb-6 space-y-2">
          {alerts.map((a, i) => (
            <div key={i} className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm font-medium ${
              a.type === "warning" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-blue-50 border-blue-200 text-blue-800"
            }`}>
              <FiBell size={16} className="flex-shrink-0 mt-0.5" />
              {a.msg}
            </div>
          ))}
        </div>
      )}

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">

        {/* LEFT */}
        <div className="space-y-6">

          {/* Emotional Wellbeing Trend Card */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 overflow-hidden">

              {/* Card header: title + child chips + Ensightly toggle */}
              <div className="px-6 pt-5 pb-4 border-b border-slate-100 space-y-3">
                <div className="flex flex-wrap justify-between items-center gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-base font-bold text-slate-900">
                      <FiTrendingUp size={16} className="text-[#1ecab8]" />
                      <span>Emotional Wellbeing Trend</span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {visibleChildren.length === 1 && !showEnsightlyAvg
                        ? `Each dot = one month · ${visibleChildren[0]?.name ?? ""}`
                        : `Showing ${visibleChildren.length} child${visibleChildren.length === 1 ? "" : "ren"}${showEnsightlyAvg ? " + Ensightly Average" : ""}`}
                    </p>
                  </div>

                  {/* Child selector chips + Ensightly Average toggle */}
                  <div className="flex flex-wrap items-center gap-2">
                    {children.length > 1 && (
                      <button
                        type="button"
                        onClick={selectAllChildren}
                        className={`text-xs px-2.5 py-1 rounded-full font-semibold border transition-all ${
                          selectedChildIds.size === children.length
                            ? "bg-slate-800 text-white border-slate-800 shadow-xs"
                            : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                        }`}
                      >
                        All ({children.length})
                      </button>
                    )}

                    {children
                      .filter((c) => (c.timeline || []).length > 0)
                      .map((c) => {
                        const ci = children.indexOf(c);
                        const color = CHILD_COLORS[ci % CHILD_COLORS.length];
                        const isOn  = selectedChildIds.has(c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => toggleChild(c.id)}
                            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-all duration-150 shadow-xs"
                            style={isOn
                              ? { background: color, borderColor: color, color: "#fff" }
                              : { background: "transparent", borderColor: color, color }
                            }
                          >
                            <span className="w-2 h-2 rounded-full" style={{ background: isOn ? "#fff" : color }} />
                            {c.name.split(" ")[0]}
                          </button>
                        );
                      })}

                    {/* Ensightly Benchmark Toggle */}
                    <button
                      type="button"
                      onClick={() => setShowEnsightlyAvg(!showEnsightlyAvg)}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border transition-all duration-200 ${
                        showEnsightlyAvg
                          ? "bg-slate-900 border-slate-900 text-white shadow-sm"
                          : "bg-white border-slate-300 text-slate-600 hover:border-slate-400 hover:bg-slate-50"
                      }`}
                    >
                      <FiGlobe size={13} className={showEnsightlyAvg ? "text-[#1ecab8]" : "text-slate-400"} />
                      <span>Ensightly Average</span>
                      {benchLoading && (
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin ml-0.5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Ensightly Benchmark Filter Panel (conditionally shown when showEnsightlyAvg is active) */}
                {showEnsightlyAvg && (
                  <div className="bg-slate-50/90 border border-slate-200/80 rounded-xl p-3.5 transition-all space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700">
                        <FiSliders size={13} className="text-[#1ecab8]" />
                        <span>Ensightly Average Benchmark Filters</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-medium">
                        Filters apply strictly to the Ensightly Average line (wards remain actual)
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                      <ChartFilterDropdown
                        label="Country"
                        value={benchCountry}
                        options={BENCH_COUNTRY_OPTIONS}
                        onChange={setBenchCountry}
                      />
                      <ChartFilterDropdown
                        label="State"
                        value={benchState}
                        options={BENCH_STATE_OPTIONS}
                        onChange={setBenchState}
                      />
                      <ChartFilterDropdown
                        label="Area"
                        value={benchArea}
                        options={BENCH_AREA_OPTIONS}
                        onChange={setBenchArea}
                      />
                      <ChartFilterDropdown
                        label="Board"
                        value={benchBoard}
                        options={BENCH_BOARD_OPTIONS}
                        onChange={setBenchBoard}
                      />
                      <ChartFilterDropdown
                        label="Grade"
                        value={benchGrade}
                        options={BENCH_GRADE_OPTIONS}
                        onChange={setBenchGrade}
                      />
                    </div>

                    {/* Benchmark Status & Empty state guidance */}
                    {benchLoading ? (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-white/80 border border-slate-200 text-xs text-slate-500 font-semibold animate-pulse">
                        <div className="w-3.5 h-3.5 border-2 border-[#1ecab8] border-t-transparent rounded-full animate-spin shrink-0" />
                        <span>Fetching Ensightly benchmark data for selected filters...</span>
                      </div>
                    ) : ensightlyTimeline.length === 0 ? (
                      <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs">
                        <div className="flex items-center gap-2 text-amber-800 font-medium">
                          <FiAlertCircle size={15} className="text-amber-600 shrink-0" />
                          <span>
                            <strong>No benchmark data found</strong> for{" "}
                            {activeBenchmarkFilters.length > 0
                              ? activeBenchmarkFilters.join(" · ")
                              : "the selected filters"}
                            . The benchmark line is hidden because there are no matching student mood logs.
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setBenchCountry("All Countries");
                            setBenchState("All States");
                            setBenchArea("All Areas");
                            setBenchBoard("All Boards");
                            setBenchGrade("All Grades");
                          }}
                          className="px-2.5 py-1 text-[11px] font-bold bg-white hover:bg-amber-100/60 text-amber-900 border border-amber-300 rounded-md transition-colors shadow-2xs cursor-pointer"
                        >
                          Reset Filters to All
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2 text-[11px] px-1">
                        <span className="text-emerald-700 font-semibold flex items-center gap-1.5">
                          <FiCheck size={13} className="text-emerald-600" />
                          Benchmark active · {ensightlyTimeline.length} data points{" "}
                          {activeBenchmarkFilters.length > 0
                            ? `(${activeBenchmarkFilters.join(" · ")})`
                            : "(National Overall)"}
                        </span>
                        {activeBenchmarkFilters.length > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              setBenchCountry("All Countries");
                              setBenchState("All States");
                              setBenchArea("All Areas");
                              setBenchBoard("All Boards");
                              setBenchGrade("All Grades");
                            }}
                            className="text-[10px] font-bold text-slate-500 hover:text-slate-800 underline cursor-pointer"
                          >
                            Reset to National
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Inner content */}
              <div className="px-6 py-5">

                {/* Single Child Trend badge + Summary stats */}
                {visibleChildren.length === 1 && !showEnsightlyAvg && monthlyData.length > 0 && (
                  <div className="mb-5">
                    {/* IMPROVING / DECLINING badge */}
                    <div className="flex items-center gap-2 mb-4">
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold"
                        style={{
                          background: trendDir === "up" ? "#dcfce7" : trendDir === "down" ? "#fee2e2" : "#f1f5f9",
                          color:      trendDir === "up" ? "#15803d" : trendDir === "down" ? "#b91c1c"  : "#64748b",
                        }}
                      >
                        {trendDir === "up" ? "↑ IMPROVING" : trendDir === "down" ? "↓ DECLINING" : "→ STABLE"}
                      </span>
                    </div>

                    {/* Best / Latest / Worst row */}
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { label: "BEST MONTH",  data: bestMonth,   accent: "#3b82f6" },
                        { label: "LATEST",       data: latestMonth, accent: "#1ecab8", dot: true },
                        { label: "WORST MONTH", data: worstMonth,  accent: "#f59e0b" },
                      ].map(({ label, data, accent, dot }) => {
                        if (!data) return null;
                        const zl = scoreToZoneLabel(data.score);
                        return (
                          <div key={label}>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">{label}</div>
                            <div className="flex items-center gap-1.5">
                              {dot && <span className="w-2 h-2 rounded-full" style={{ background: zl.color }} />}
                              <span className="text-sm font-bold" style={{ color: zl.color }}>{zl.name}</span>
                            </div>
                            <div className="text-[11px] text-slate-400 mt-0.5">{data.label} · {data.score.toFixed(1)}/7</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Multi-Series Comparison Legend Bar */}
                {(visibleChildren.length > 1 || showEnsightlyAvg) && monthlyData.length > 0 && (
                  <div className="mb-5 flex flex-wrap items-center gap-2.5 bg-slate-50/80 p-3 rounded-xl border border-slate-200/80">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider mr-1">Active Lines:</span>
                    {visibleChildren.map((c) => {
                      const ci = children.indexOf(c);
                      const color = CHILD_COLORS[ci % CHILD_COLORS.length];
                      const { color: zColor } = getZoneColor(c.zone);
                      return (
                        <div
                          key={c.id}
                          className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-xs text-xs"
                        >
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                          <span className="font-bold text-slate-800">{c.name.split(" ")[0]}</span>
                          <span className="text-slate-300">·</span>
                          <span className="font-semibold text-slate-600">
                            {c.ensightlyScore ? `${c.ensightlyScore.toFixed(1)}/7` : "No logs"}
                          </span>
                          <span
                            className="text-[10px] font-bold px-1.5 py-0.2 rounded"
                            style={{ background: `${zColor}18`, color: zColor }}
                          >
                            {c.zone || "Zone"}
                          </span>
                        </div>
                      );
                    })}

                    {showEnsightlyAvg && (
                      <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-xs text-xs">
                        <span className="w-3 h-0.5 bg-slate-900 border-b-2 border-dashed border-slate-900" />
                        <span className="font-bold text-slate-900">Ensightly Avg</span>
                        <span className="text-slate-300">·</span>
                        {benchLoading ? (
                          <span className="text-slate-400 font-medium animate-pulse">Loading...</span>
                        ) : ensightlyTimeline.length > 0 ? (
                          <>
                            <span className="font-semibold text-slate-700">
                              {(
                                ensightlyTimeline.reduce((s, p) => s + p.score, 0) /
                                ensightlyTimeline.length
                              ).toFixed(1)}/7
                            </span>
                            <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-slate-100 text-slate-600">
                              {activeBenchmarkFilters.length > 0 ? "Filtered" : "National"}
                            </span>
                          </>
                        ) : (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1">
                            <FiAlertCircle size={11} className="shrink-0 text-amber-600" />
                            No data for selected filters
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Area / Multi-Line Chart */}
                {monthlyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={monthlyData} margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
                      <defs>
                        {children.map((c) => {
                          const ci = children.indexOf(c);
                          const color = CHILD_COLORS[ci % CHILD_COLORS.length];
                          return (
                            <linearGradient key={c.id} id={`childGrad_${c.id}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor={color} stopOpacity={0.25} />
                              <stop offset="95%" stopColor={color} stopOpacity={0.02} />
                            </linearGradient>
                          );
                        })}
                      </defs>

                      {/* Zone bands — boundaries match DB thresholds on joyfulness 1-7 scale */}
                      <ReferenceArea y1={6.16} y2={7}    fill="#10b98115" />  {/* Green Zone  */}
                      <ReferenceArea y1={5.25} y2={6.16} fill="#3b82f615" />  {/* Blue Zone   */}
                      <ReferenceArea y1={4.41} y2={5.25} fill="#f59e0b12" />  {/* Yellow Zone */}
                      <ReferenceArea y1={2.66} y2={4.41} fill="#8b451312" />  {/* Brown Zone  */}
                      <ReferenceArea y1={1.75} y2={2.66} fill="#ef444412" />  {/* Red Zone    */}
                      <ReferenceArea y1={0}    y2={1.75} fill="#dc262612" />  {/* Dark Red    */}

                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: "#94a3b8" }}
                        tickLine={false}
                        axisLine={false}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        type="number"
                        domain={[() => 0.8, () => 7]}
                        // Place ticks at the vertical midpoint of each zone band
                        ticks={[1.37, 2.2, 3.53, 4.83, 5.7, 6.58]}
                        tick={<ZoneYTick />}
                        tickLine={false}
                        axisLine={false}
                        width={68}
                      />
                      <Tooltip content={<TrendTooltip />} />

                      {/* Render Each Selected Child */}
                      {visibleChildren.map((c) => {
                        const ci = children.indexOf(c);
                        const color = CHILD_COLORS[ci % CHILD_COLORS.length];
                        const isSingle = visibleChildren.length === 1 && !showEnsightlyAvg;
                        return (
                          <Area
                            key={c.id}
                            type="monotone"
                            dataKey={`child_${c.id}`}
                            name={c.name}
                            stroke={color}
                            strokeWidth={2.5}
                            fill={isSingle ? `url(#childGrad_${c.id})` : "none"}
                            dot={{ r: 4, fill: color, stroke: "#fff", strokeWidth: 2 }}
                            activeDot={{ r: 6, fill: color, stroke: "#fff", strokeWidth: 2 }}
                            connectNulls
                          />
                        );
                      })}

                      {/* Render Ensightly Average Benchmark Line */}
                      {showEnsightlyAvg && (
                        <Area
                          key="ensightlyAvg"
                          type="monotone"
                          dataKey="ensightlyAvg"
                          name="Ensightly Average"
                          stroke="#0f172a"
                          strokeWidth={2.5}
                          strokeDasharray="5 5"
                          fill="none"
                          dot={{ r: 4, fill: "#0f172a", stroke: "#fff", strokeWidth: 2 }}
                          activeDot={{ r: 6, fill: "#0f172a", stroke: "#fff", strokeWidth: 2 }}
                          connectNulls
                        />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[240px] text-slate-400 text-sm gap-2">
                    <FiTrendingUp size={32} className="opacity-30" />
                    <span>No trend data available for the selected children in this period.</span>
                  </div>
                )}
              </div>
            </div>

        {/* Children Cards */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80">
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                <FiUsers size={18} />
                <span>My Children</span>
              </div>
              <Link to="/parent/children" className="text-sm text-[#1ecab8] font-medium hover:underline flex items-center gap-1">
                View All <FiChevronRight size={14} />
              </Link>
            </div>

            {children.length === 0 ? (
              <div className="text-center py-10 text-slate-400">
                <FiUsers size={40} className="mx-auto mb-3 opacity-30" />
                <p>No children linked to your account yet.</p>
              </div>
            ) : (
              <div className="space-y-5">
                {children.map((child, ci) => {
                  const { color } = getZoneColor(child.zone);
                  const scorePct = scoreToPercent(child.ensightlyScore);
                  const categories = child.categories || [];
                  return (
                    <div key={child.id} className="border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-4">
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-white flex-shrink-0"
                            style={{ background: color }}
                          >
                            {child.name.charAt(0)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-slate-900 text-base">{child.name}</h3>
                              {child.needsAttention && (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-semibold">
                                  Needs Attention
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">{child.grade} • {child.school}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <ZoneBadge zone={child.zone} />
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <FiClock size={11} /> {child.lastLogged}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div
                            className="w-14 h-14 rounded-full border-4 flex items-center justify-center font-bold text-sm"
                            style={{ borderColor: color, color }}
                          >
                            {scorePct}%
                          </div>
                          <span className="text-xs text-slate-400 mt-1">Score</span>
                        </div>
                      </div>

                      {/* Category bars — shimmer while enrichment loads */}
                      {enrichLoading && categories.length === 0 ? (
                        <div className="mb-4 space-y-2">
                          {[0,1,2,3].map(i => <Shimmer key={i} className="h-3 w-full" />)}
                        </div>
                      ) : categories.length > 0 ? (
                        <div className="mb-4">
                          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                            Category Breakdown
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                            {categories.map((cat) => <CategoryBar key={cat.catId} cat={cat} />)}
                          </div>
                        </div>
                      ) : null}

                      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                        <div className="flex gap-5">
                          <div>
                            <div className="text-xs text-slate-400 mb-0.5">Mood Logs</div>
                            <div className="text-sm font-bold text-slate-800">{child.totalLogs}</div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-400 mb-0.5">Categories</div>
                            <div className="text-sm font-bold text-slate-800">{categories.length || "—"}</div>
                          </div>
                        </div>
                        <Link
                          to={`/parent/children/${child.id}/mood-tracking`}
                          className="flex items-center gap-1.5 text-sm font-semibold text-[#1ecab8] hover:text-[#1bb8a6] transition-colors"
                        >
                          View Details <FiChevronRight size={15} />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT */}
        <div className="space-y-6">

          {/* ── AI Insight Card ─────────────────────────────────────── */}
          {(() => {
            const aiChild = children.find(c => c.id === aiInsightChildId) ?? children[0] ?? null;
            if (!aiChild) return null;

            // Compute trend for this child from their monthly data
            const childTimeline = aiChild.timeline ?? [];
            const childBuckets: Record<string, { sum: number; count: number }> = {};
            childTimeline.forEach(pt => {
              const k = pt.date.slice(0, 7);
              if (!childBuckets[k]) childBuckets[k] = { sum: 0, count: 0 };
              childBuckets[k].sum += pt.score;
              childBuckets[k].count += 1;
            });
            const childMonthly = Object.entries(childBuckets)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([, { sum, count }]) => sum / count);
            const childTrend: "up" | "down" | "stable" =
              childMonthly.length < 2 ? "stable"
              : childMonthly[childMonthly.length - 1] - childMonthly[0] > 0.15 ? "up"
              : childMonthly[childMonthly.length - 1] - childMonthly[0] < -0.15 ? "down"
              : "stable";

            const insight = generateInsight(aiChild, childTrend);
            const toneStyles = {
              positive: { border: "border-emerald-200", bg: "bg-emerald-50", badge: "bg-emerald-100 text-emerald-700", icon: "text-emerald-500" },
              neutral:  { border: "border-slate-200",   bg: "bg-slate-50",   badge: "bg-slate-100 text-slate-600",   icon: "text-slate-400" },
              caution:  { border: "border-amber-200",   bg: "bg-amber-50",   badge: "bg-amber-100 text-amber-700",   icon: "text-amber-500" },
              alert:    { border: "border-rose-200",    bg: "bg-rose-50",    badge: "bg-rose-100 text-rose-700",     icon: "text-rose-500" },
            };
            const ts = toneStyles[insight.tone];

            return (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 overflow-hidden">
                {/* Header */}
                <div className={`px-5 pt-4 pb-3 border-b ${ts.border} ${ts.bg}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <FiCpu size={16} className={ts.icon} />
                      <span className="text-sm font-bold text-slate-800">AI Insight</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ts.badge}`}>
                        {insight.headline}
                      </span>
                    </div>
                    {/* Child switcher chips */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {children.map((c, ci) => {
                        const col = CHILD_COLORS[ci % CHILD_COLORS.length];
                        const isActive = c.id === aiInsightChildId;
                        return (
                          <button
                            key={c.id}
                            onClick={() => setAiInsightChildId(c.id)}
                            className="text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-all"
                            style={isActive
                              ? { background: col, borderColor: col, color: "#fff" }
                              : { background: "transparent", borderColor: col, color: col }
                            }
                          >
                            {c.name.split(" ")[0]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="px-5 py-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                      style={{ background: getZoneColor(aiChild.zone).color }}
                    >
                      {aiChild.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-slate-500 mb-1">
                        {aiChild.name.split(" ")[0]} · {aiChild.zone || "No Zone"} ·{" "}
                        <span className="font-bold" style={{ color: getZoneColor(aiChild.zone).color }}>
                          {Math.round(((aiChild.ensightlyScore ?? 0) / 7) * 100)}%
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{insight.body}</p>
                    </div>
                  </div>

                  {/* Action suggestion */}
                  <div className={`flex items-start gap-2 mt-3 px-3 py-2.5 rounded-lg ${ts.bg} border ${ts.border}`}>
                    <FiZap size={13} className={`${ts.icon} flex-shrink-0 mt-0.5`} />
                    <p className="text-xs text-slate-600 leading-relaxed">
                      <span className="font-semibold">Suggested action: </span>
                      {insight.action}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Alerts */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80">
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                <FiBell size={18} />
                <span>Alerts</span>
              </div>
              <Link to="/parent/notifications" className="text-sm text-[#1ecab8] font-medium hover:underline flex items-center gap-1">
                View All <FiChevronRight size={14} />
              </Link>
            </div>
            {alerts.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <FiCheckCircle size={24} className="text-green-500" />
                </div>
                <p className="text-sm font-semibold text-slate-700">All Good!</p>
                <p className="text-xs text-slate-400 mt-1">No alerts for now.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((a, i) => (
                  <div key={i} className={`flex items-start gap-2.5 px-3 py-3 rounded-lg text-xs font-medium border ${
                    a.type === "warning" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-blue-50 border-blue-200 text-blue-800"
                  }`}>
                    <FiBell size={14} className="flex-shrink-0 mt-0.5" />
                    {a.msg}
                  </div>
                ))}
              </div>
            )}
            <Link
              to="/parent/notifications"
              className="text-sm text-[#1ecab8] font-medium hover:underline block text-center pt-4 mt-4 border-t border-slate-100"
            >
              View All Notifications →
            </Link>
          </div>

          {/* Family Summary */}
          {children.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80">
              <div className="flex items-center gap-2 text-lg font-semibold text-slate-900 mb-5">
                <FiBarChart2 size={18} />
                <span>Family Summary</span>
              </div>
              <div className="space-y-4">
                {children.map((child, ci) => {
                  const { color } = getZoneColor(child.zone);
                  const pct = scoreToPercent(child.ensightlyScore);
                  return (
                    <div key={child.id}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: CHILD_COLORS[ci % CHILD_COLORS.length] }} />
                          <span className="text-sm font-medium text-slate-700">{child.name.split(" ")[0]}</span>
                        </div>
                        <span className="text-xs text-slate-500">{pct}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div
                          className="h-2 rounded-full transition-all duration-700"
                          style={{ width: `${pct}%`, background: color }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent Activity — moved to bottom */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80">
            <div className="flex items-center gap-2 text-lg font-semibold text-slate-900 mb-5">
              <FiActivity size={18} />
              <span>Recent Activity</span>
            </div>
            {enrichLoading && allRecentActivity.length === 0 ? (
              <div className="space-y-4">
                {[0,1,2,3].map(i => (
                  <div key={i} className="flex gap-3">
                    <Shimmer className="w-9 h-9 rounded-lg flex-shrink-0" />
                    <div className="flex-1">
                      <Shimmer className="h-4 w-28 mb-1" />
                      <Shimmer className="h-3 w-36" />
                    </div>
                  </div>
                ))}
              </div>
            ) : allRecentActivity.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">
                <FiActivity size={32} className="mx-auto mb-3 opacity-30" />
                No recent logs found.
              </div>
            ) : (
              <div className="space-y-3">
                {allRecentActivity.map((log, i) => {
                  const { color } = getZoneColor(log.calculatedZone);
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ background: color }}
                      >
                        {log.childName.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-semibold text-slate-800">{log.childName.split(" ")[0]}</span>
                          <ZoneBadge zone={log.calculatedZone} />
                        </div>
                        <div className="text-xs text-slate-500">
                          {log.category?.name || "General"} • {log.calculatedEmotion || "—"}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">{timeAgo(log.date, log.time)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ParentDashboard;
